import React, { useContext, useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectContainer from '../../components/containers/ProjectContainer';
import CalendarView from './components/CalendarView';
import useCalendar from './useCalendar';
import { useQuery } from '../../router';
import CreateOrLinkLesson from './components/CreateOrLinkLesson';
import ProjectCalendarPicker from '../../components/dateAndTime/ProjectCalendarPicker';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import { getCalendarLessons } from '../../api/lesson';
import {
  getDateInUTC,
  getEndOfDay,
  getEndOfMonth,
  getStartOfMonth,
  parseDateToStringWithFormat,
} from '../../utils/dateFormat';
import { CalendarEvent } from '../../api/types/event';
import CreateOrEditLessonModal from '../../components/modals/CreateOrEditLessonModal';
import useModalState from '../../hooks/useModalState';
import { Lesson } from '../../api/types/lesson';
import ProjectTableModal from '../../components/ProjectTableModal';
import { modalConfigs } from '../../components/ProjectTableModal/constants';
import { ProjectTableDataType } from '../../components/ProjectTable/types';
import { LARGE_PROJECT_MODAL_WIDTH } from '../../utils/styled/dimensions';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/icon-angle-right.svg';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/icon-angle-left.svg';
import { format } from '../../utils/dateFormat/types';

const DailyAluxCalendar = () => {
  const query = useQuery();
  const initialSelectedDate = query.get('selectedDate') as string | undefined;
  const initialDate = initialSelectedDate ? new Date(initialSelectedDate) : new Date();

  const { setIsLoading } = useContext(ActivityIndicatorContext);

  const [didStartFlowWithDate, setDidStartFlowWithDate] = useState<Date | undefined>();

  const editLessonModalProps = useModalState<{ lesson?: Lesson; publishedAtDate?: Date }>();
  const seeMoreLessonsProps = useModalState<{ startDate: Date }>();

  const refreshLessons = async () => {
    const startDate = getDateInUTC(getStartOfMonth(selectedDate));
    const endDate = getDateInUTC(getEndOfMonth(selectedDate));

    setIsLoading(true);
    const result = await getCalendarLessons(startDate, endDate);
    setIsLoading(false);

    if (result && Array.isArray(result)) {
      const processedEvents: CalendarEvent[] = result?.map((item) => ({
        start: item?.publishedAtDate,
        end: getEndOfDay(item?.publishedAtDate),
        data: item,
      }));
      updateEvents(processedEvents);
    } else {
      updateEvents([]);
    }
  };

  const {
    selectedDate,
    eventsData,
    updateEvents,
    onSelectDateFromCalendar,
    onNextPress,
    onPreviousPress,
    onTodayPress,
  } = useCalendar({ refreshEvents: refreshLessons, initialDate });

  useEffect(() => {
    refreshLessons().then();
  }, []);

  const onSelectEvent = (event: CalendarEvent) => {
    editLessonModalProps.open({ lesson: event.data });
  };

  const onSelectSlot = (date: Date) => {
    const activeYear = selectedDate.getFullYear();
    const activeMonth = selectedDate.getMonth();
    const clickedYear = date.getFullYear();
    const clickedMonth = date.getMonth();
    if (clickedYear === activeYear && clickedMonth === activeMonth) {
      setDidStartFlowWithDate(date);
    } else {
      onSelectDateFromCalendar(date);
    }
  };

  const onShowMoreEvents = (date: Date) => {
    seeMoreLessonsProps.open({ startDate: date });
  };

  const onViewLessonDetails = (data: ProjectTableDataType | string[]) => {};

  return (
    <ProjectContainer title={'Daily Alux Calendar'}>
      <Components.Container>
        <Components.BodyContainer displayFlex>
          <Components.LeftContainer>
            <Components.ProjectCalendarWrapper>
              <ProjectCalendarPicker
                dateValue={selectedDate}
                onDateValueChange={onSelectDateFromCalendar}
                valueDisplayFormat={'month'}
              />
              <Components.IconButton onClick={onPreviousPress}>
                <ArrowLeftIcon width={24} height={24} />
              </Components.IconButton>
              <Components.IconButton onClick={onNextPress}>
                <ArrowRightIcon />
              </Components.IconButton>
              <Components.Separator />
              <Components.TodayButton onClick={onTodayPress}>Today</Components.TodayButton>
            </Components.ProjectCalendarWrapper>
          </Components.LeftContainer>
          <CalendarView
            onSelectEvent={onSelectEvent}
            selectedDate={selectedDate}
            events={eventsData}
            onShowMore={onShowMoreEvents}
            onSelectSlot={onSelectSlot}
          />
        </Components.BodyContainer>
      </Components.Container>
      <CreateOrLinkLesson
        didStartFlow={didStartFlowWithDate}
        setDidStartFlow={setDidStartFlowWithDate}
        refreshCallback={refreshLessons}
      />
      <CreateOrEditLessonModal {...editLessonModalProps} refreshCallback={refreshLessons} />
      <ProjectTableModal
        modalConfig={modalConfigs.showMoreCalendarLessons()}
        {...seeMoreLessonsProps}
        onModalSubmit={onViewLessonDetails}
        modalTitle={
          seeMoreLessonsProps?.modalState?.startDate
            ? `See the lessons published on ${parseDateToStringWithFormat(
                seeMoreLessonsProps?.modalState?.startDate,
                format.monthDayYear,
              )}`
            : 'See published lessons'
        }
        modalWidth={LARGE_PROJECT_MODAL_WIDTH}
        singleSelectionOnly
      />
    </ProjectContainer>
  );
};

export default DailyAluxCalendar;
